import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { LockedOverlay } from "@/components/plan-mini/locked-overlay";
import { useViewerDateFnsLocale } from "@/lib/translator";
import useSizingDailyRecommendations from "@babackapp/private-api-client/client/use-sizing-daily-recommendations";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@babackapp/ui/components/card";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
} from "@babackapp/ui/components/chart";
import { format, subDays } from "date-fns";
import { useMemo } from "react";
import { Bar, BarChart, CartesianGrid, Cell, XAxis, YAxis } from "recharts";
import { MOCK_DAILY_RECOMMENDATIONS, USE_MOCK_DATA } from "../lib/mock-data";

const chartConfig = {
  count: {
    label: "Recommandations",
    color: "hsl(239 84% 67%)",
  },
} satisfies ChartConfig;

type Props = {
  isLocked?: boolean;
};

export function SizingDailyRecommendationsChart({ isLocked = false }: Props) {
  const { data: shop } = useCurrentShopSuspense();
  const locale = useViewerDateFnsLocale();

  const { fromTimestamp, toTimestamp } = useMemo(() => {
    const to = Date.now();
    return { fromTimestamp: subDays(to, 30).getTime(), toTimestamp: to };
  }, []);

  const apiResult = useSizingDailyRecommendations({
    shopId: shop.id,
    fromTimestamp,
    toTimestamp,
  });

  const data = useMemo(() => {
    const raw =
      isLocked || USE_MOCK_DATA
        ? MOCK_DAILY_RECOMMENDATIONS.slice(-30)
        : (apiResult.data ?? []);

    const byDay = new Map(raw.map((d) => [d.date, d.count]));

    const DAY = 86_400_000;
    const startOfDay = (ts: number) => ts - (ts % DAY);

    return Array.from({ length: 30 }, (_, i) => {
      const date = startOfDay(fromTimestamp + i * DAY);
      return { date, count: byDay.get(date) ?? 0 };
    });
  }, [isLocked, apiResult.data, fromTimestamp]);

  const tickInterval = 4;

  const total = data.reduce((sum, d) => sum + d.count, 0);

  return (
    <Card className="col-span-4 relative overflow-hidden">
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle className="text-md">Recommandations — 30 derniers jours</CardTitle>
        <span className="text-sm text-muted-foreground tabular-nums">
          {total.toLocaleString()} au total
        </span>
      </CardHeader>
      <CardContent className="h-52 w-full pt-2">
        <ChartContainer config={chartConfig} className="h-full w-full">
          <BarChart data={data} barCategoryGap="20%">
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              interval={tickInterval}
              tickFormatter={(value) =>
                format(new Date(value), "d MMM", { locale })
              }
              className="text-xs"
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickMargin={4}
              width={30}
              allowDecimals={false}
              className="text-xs"
            />
            <ChartTooltip
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                const ts = Number(label);
                const date =
                  isFinite(ts) && ts > 0
                    ? format(new Date(ts), "d MMMM yyyy", { locale })
                    : String(label);
                const count = (payload[0]?.value as number) ?? 0;
                return (
                  <div className="rounded-lg border bg-background px-3 py-2 shadow-md text-sm">
                    <p className="font-medium text-foreground">{date}</p>
                    <p className="text-muted-foreground">
                      {count}{" "}
                      {count === 1 ? "recommandation" : "recommandations"}
                    </p>
                  </div>
                );
              }}
            />
            <Bar dataKey="count" radius={[4, 4, 0, 0]}>
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={
                    entry.count === 0
                      ? "hsl(239 84% 67% / 0.15)"
                      : "hsl(239 84% 67%)"
                  }
                />
              ))}
            </Bar>
          </BarChart>
        </ChartContainer>
      </CardContent>
      {isLocked && (
        <LockedOverlay
          title="Données disponibles avec le Sizing"
          description="Activez le Sizing pour voir le nombre de recommandations de taille faites à vos acheteurs jour par jour."
        />
      )}
    </Card>
  );
}
